import { Button } from '@synoptic/ui-kit/button/button.js';
import { CircleSparkIcon } from '@synoptic/ui-kit/icons/react/circle-spark.js';
import { EditIcon } from '@synoptic/ui-kit/icons/react/edit.js';
import { PlusIcon } from '@synoptic/ui-kit/icons/react/plus.js';
import { LinkButton } from '@synoptic/ui-kit/link-button/link-button.js';
import {
  Select,
  SelectContent,
  SelectTrigger,
} from '@synoptic/ui-kit/select/select.js';
import { Spinner } from '@synoptic/ui-kit/spinner/spinner.js';
import { themeVars } from '@synoptic/ui-kit/theme/vars.js';
import { useMemo } from 'react';
import { useAuthGuard } from '@/components/auth-guard/auth-guard-provider';
import { ManageFilterModal } from './manage-filter/manage-filter';
import { ManageFiltersModal } from './manage-filters/manage-filters';
import { AiFilter } from './types';
import { useAIFilters } from './use-ai-filters';
import { useCreateAiFilter } from './use-create-ai-filter';
import {
  aiFiltersContainer,
  aiFilterSelectItem,
  aiFilterSelectTrigger,
  aiFiltersFooter,
  aiFiltersHeader,
  aiFiltersHeaderAside,
  aiFiltersHeaderTitle,
  aiFiltersLoading,
  filterSelectContent,
  filterSelectName,
  filterSelectPrompt,
} from './ai-filters.css';

interface UseAIFiltersBlockProps {
  selectedFilter?: AiFilter | null;
  onSelectFilter: (filter: AiFilter | '') => void;
  /** No filters are loaded and none can be picked. */
  disabled?: boolean;
}

export const AIFilters = ({
  onSelectFilter,
  selectedFilter,
  disabled = false,
}: UseAIFiltersBlockProps) => {
  const guard = useAuthGuard();
  const { aiFilters, loading } = useAIFilters(disabled);

  const { createFilter } = useCreateAiFilter(onSelectFilter);

  const existingSelectedFilter = useMemo(() => {
    if (!selectedFilter || !aiFilters?.length) {
      return null;
    }
    return aiFilters?.find((filter) => filter.id === selectedFilter?.id);
  }, [aiFilters, selectedFilter]);

  const filterSelectOptions = useMemo(() => {
    return aiFilters.length > 0
      ? aiFilters.map(({ name, id, prompt }) => ({
          // TODO: add support for item's description in ui-kit if there will be more cases like this
          content: (
            <div className={filterSelectContent}>
              <p className={filterSelectName}>{name}</p>
              <p title={prompt} className={filterSelectPrompt}>
                {prompt}
              </p>
            </div>
          ),
          value: id,
        }))
      : [
          {
            content: 'No Filters exists',
            value: 'NO_FILTERS',
            disabled: true,
          },
        ];
  }, [aiFilters]);

  return (
    <div className={aiFiltersContainer}>
      <header className={aiFiltersHeader}>
        <div className={aiFiltersHeaderAside}>
          <CircleSparkIcon
            color={themeVars.container.foreground.brand}
            width={16}
            height={16}
          />
          <h2 className={aiFiltersHeaderTitle}>Use AI filter</h2>
        </div>
      </header>
      {loading ? (
        <div className={aiFiltersLoading}>
          <Spinner />
        </div>
      ) : (
        <Select
          disabled={disabled}
          value={existingSelectedFilter?.id || ''}
          onValueChange={(selectedFilterId) => {
            const filter = aiFilters.find(
              (filter) => filter.id === selectedFilterId,
            );
            if (filter) {
              onSelectFilter(filter);
            }
          }}
        >
          <SelectTrigger
            placeholder={'Select filter'}
            className={aiFilterSelectTrigger}
            triggerText={existingSelectedFilter?.name}
            variant={'outline'}
            onClear={() => {
              onSelectFilter('');
            }}
          />
          <SelectContent
            position="popper"
            className={aiFilterSelectItem}
            items={filterSelectOptions}
          />
        </Select>
      )}
      <footer className={aiFiltersFooter}>
        <ManageFiltersModal createFilter={createFilter} aiFilters={aiFilters}>
          <LinkButton
            disabled={aiFilters.length === 0}
            startIcon={<EditIcon />}
          >
            Edit existing filters
          </LinkButton>
        </ManageFiltersModal>
        <ManageFilterModal onSubmit={createFilter}>
          <Button
            startIcon={<PlusIcon />}
            variant="secondary"
            size="small"
            onClick={(event) => {
              if (guard()) event.preventDefault();
            }}
          >
            Create new AI filter
          </Button>
        </ManageFilterModal>
      </footer>
    </div>
  );
};
